import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { getUser, setUser as setUserInStorage, clearTokens, redirectToLogin } from '@myapp/common-web-sdk';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const getRoleCodes = (u) => {
    if (!u) return [];
    if (Array.isArray(u.roles_info)) {
      return u.roles_info.map((r) => r.code || r.name).filter(Boolean);
    }
    if (Array.isArray(u.roles)) {
      return u.roles.map((r) => (typeof r === 'string' ? r : r.code || r.name)).filter(Boolean);
    }
    return [];
  };

  // 初始化:从 SDK 获取用户信息 + 静默验证token
  useEffect(() => {
    const initAuth = async () => {
      const storedUser = getUser();

      // 方案3: 乐观显示 - 立即显示用户信息(如果存在)
      if (storedUser) {
        setUser(storedUser);
      }

      // 立即结束loading状态,让页面快速渲染
      setLoading(false);

      // 静默验证: 在后台验证token有效性
      if (storedUser) {
        try {
          // 调用API验证token,如果token过期会自动触发refresh
          const response = await authAPI.getProfile();
          // 验证成功,更新用户信息(可能有新数据)
          setUser(response.data);
          setUserInStorage(response.data);
        } catch (err) {
          // 验证失败(token过期且refresh失败)
          console.warn('Token validation failed, clearing auth state:', err);
          // 清除本地状态
          clearTokens();
          setUser(null);
          // 跳转到登录页
          redirectToLogin();
        }
      }
    };

    initAuth();
  }, []);

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch (err) {
      // 即使登出请求失败，也清除本地状态
      console.error('Logout error:', err);
    } finally {
      clearTokens();
      setUser(null);
      // 使用 SDK 跳转到登录页，登录后跳转到配置的前缀路径
      redirectToLogin(process.env.PUBLIC_URL || '/');
    }
  }, []);

  const updateUser = useCallback((userData) => {
    setUser(userData);
    setUserInStorage(userData);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const response = await authAPI.getProfile();
      setUser(response.data);
      setUserInStorage(response.data);
      return response.data;
    } catch (err) {
      console.error('Refresh user error:', err);
      throw err;
    }
  }, []);

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    isSuperuser: !!(user?.is_superuser || getRoleCodes(user).includes('super_admin')),
    isAdmin: !!(user?.is_staff || user?.is_superuser || getRoleCodes(user).includes('admin') || getRoleCodes(user).includes('super_admin')),
    isDepartmentManager: !!getRoleCodes(user).includes('department_manager'),
    logout,
    updateUser,
    refreshUser,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
